import React, { Component } from 'react'
import { Route, Router, Switch } from 'react-router-dom'
import { observer, Provider } from 'mobx-react'
import { create } from 'mobx-persist'
import { reaction } from 'mobx'

import AppContainer from './containers/App'
import PlayerContainer from './containers/Player'
import Loading from './components/Loading'
import Api from './Api'
import history from './history'
import Analytics from './Analytics'
import AudioMessageStore from './stores/AudioMessage'
import UserStore from './stores/User'
import PlaylistStore from './stores/Playlist'
import TrackPreloaderStore from './stores/TrackPreloader'
import RecentSongStore from './stores/RecentSong'
import PlayerStore from './stores/Player'
import FranchiseStore from './stores/Franchise'
import StationStore from './stores/Station'
import StudioStore from './stores/Studio'
import { takeAppLoginRequest } from './helpers/nativeApp'
import { setUpChatPanel, setUpTypingMode } from './helpers/chatPanel'

const analytics = new Analytics()

// Mixpanel funnel: fire 'pro_started' once when an account we have seen as a
// trial or free account on this device turns into a paying Pro account. The
// status is refreshed with the sign-in token, so this catches Stripe payments
// (and anything else that makes the account Pro) without a webhook. Accounts
// that were already Pro the first time we see them don't fire, so existing
// customers don't pollute the funnel. Mixpanel funnels count unique users, so
// a second device firing it later doesn't skew the numbers.
const PRO_SEEN_KEY = 'gr_pro_status_'
const proStatus = (u) => (u.isSubscribed && !u.isTrial ? 'pro' : (u.isTrial ? 'trial' : 'free'))
// Stripe sends the gym back to play.gymradio.com/?subscribed=1 after paying.
// Mark the account Pro straight away (the sign-in token catches up a minute
// later) and record the checkout for the funnel, then tidy the address.
const takeStripeReturn = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('subscribed') !== '1') return
    params.delete('subscribed')
    const qs = params.toString()
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash)
    if (userStore.isAuthenticated) userStore.forcePaid()
    analytics.track({ event: 'pro_activated' })
  } catch (e) {}
}

const watchForProStart = () => {
  reaction(
    () => (userStore.id ? `${userStore.id}|${proStatus(userStore)}` : ''),
    (value) => {
      if (!value) return
      const [id, status] = value.split('|')
      let seen = ''
      try { seen = window.localStorage.getItem(PRO_SEEN_KEY + id) || '' } catch (e) {}
      if (status === 'pro' && (seen === 'trial' || seen === 'free')) {
        analytics.track({ event: 'pro_started', previousStatus: seen })
      }
      try { window.localStorage.setItem(PRO_SEEN_KEY + id, status) } catch (e) {}
    },
    { fireImmediately: true }
  )
}
const api = new Api()
const franchiseStore = new FranchiseStore(api)
const userStore = new UserStore(franchiseStore)
const stationStore = new StationStore(userStore)
const audioMessageStore = new AudioMessageStore(api, userStore)
const playlistStore = new PlaylistStore(api, userStore, audioMessageStore)
const trackPreloaderStore = new TrackPreloaderStore(userStore, playlistStore)
const recentSongStore = new RecentSongStore(api, playlistStore)
const playerStore = new PlayerStore(playlistStore)
const studioStore = new StudioStore(api, userStore, audioMessageStore, playlistStore)

// Persist stores
const hydrate = create({})
const trackPreloadedHydrated = hydrate('track_preloader', trackPreloaderStore, {})
const playerHydrated = hydrate('player', playerStore, {})
const stationHydrated = hydrate('station', stationStore, {})
const userHydrated = hydrate('user', userStore, {})

const handleAuthentication = async ({ location, match }) => {
  if (/access_token|id_token|error/.test(location.hash)) {
    try {
      await userStore.handleAuthentication()
    } catch (err) {
      console.error(err)
    }

    const redirectUrl = userStore.redirectUrl

    userStore.resetRedirectUrl()
    history.replace(redirectUrl)
  }
  return
}

const DefaultLayout = ({ component: Component, action, ...rest }) => {
  return (
    <Route
      { ...rest }
      render={
        (matchProps) => {
          return (
            <AppContainer>
              <Component { ...matchProps } />
            </AppContainer>
          )
        }
      }
    />
  )
}

class App extends Component {
  state = {
    storesHydrated: false
  }

  componentDidMount () {
    setUpChatPanel()
    setUpTypingMode()
    Promise
      .all([
        trackPreloadedHydrated,
        playerHydrated,
        userHydrated,
        stationHydrated
      ])
      .then(() => {
        if (takeAppLoginRequest() === 'google') {
          userStore.loginWithGoogleInApp()
          return
        }
        userStore.renewToken(() => {
          this.setState({
            storesHydrated: true
          })

          const {
            id: userId,
            email,
            franchiseId
          } = userStore

          analytics.init(userId, franchiseId, email)
          watchForProStart()
          takeStripeReturn()
        })
      })
      userStore.login
  }


  render () {
    if (!this.state.storesHydrated) {
      return <Loading />
    }
    return (
      <Provider 
        userStore={ userStore }
        stationStore={ stationStore }
        audioMessageStore={ audioMessageStore }
        studioStore={ studioStore }
        playlistStore={ playlistStore }
        recentSongStore={ recentSongStore }
        trackPreloaderStore={ trackPreloaderStore }
        playerStore={ playerStore }
        franchiseStore={ franchiseStore }
        api={ api }
      >
      <Router history={ history }>
        <Switch>
          <DefaultLayout exact path="/" component={ PlayerContainer } />
          <Route path="/callback" render={ (props) => {
            console.log(props,'props')
            handleAuthentication(props)
            
            analytics.track({ event: 'logged_in' })
            return <Loading { ...props } /> 
          }}/>
          <Route path="/franchise/:franchiseId" render={ (props) => {
            const franchiseId = props.match.params.franchiseId
            if (!userStore.isAuthenticated) {
              userStore.login('', true, franchiseId)
            } else {
              history.replace('/')
            }
            return <Loading { ...props } /> 
          }}/>
          <Route path="/subscribe/:planId" render={(props) => {
            const planId = props.match.params.planId
            // Local currency picked on the website pricing page (kept through the login redirect)
            try {
              const currency = new URLSearchParams(props.location.search).get('currency')
              if (currency) window.sessionStorage.setItem('currency', currency)
            } catch (e) {}

            if (!userStore.isAuthenticated) {
              userStore.login()
            } else {
              userStore.redirectToRecurly(planId, false)
            }
            return <Loading { ...props } />
          }}/>
          <Route path="/verify-email/success" render={(props) => {
            history.replace('/')
            analytics.track({ event: 'email_verified' })
            return <Loading {...props} /> 
          }}/>
          <Route path="/signup/success" render={(props) => {
            if (userStore.isAuthenticated) {
              userStore.forcePaid()
            }
            analytics.track({ event: 'pro_activated' })
            history.replace('/')
            return <Loading {...props} /> 
          }}/>
        </Switch>
      </Router>
      </Provider>
    )
  }
}

export default observer(App)
